import React, { useCallback, useMemo, useRef, useState } from 'react'
import DocumentChecklistRow from 'pages/ApplicationDetailsPage/components/shared/DocumentChecklistRow/DocumentChecklistRow'
import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import list from 'pages/ApplicationDetailsPage/components/shared/ListRow/ListRow.module.scss'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import GenerateLorModal from 'widgets/GenerateLorModal/GenerateLorModal'
import ResultDrawer from 'widgets/GenerateLorModal/components/ResultDrawer/ResultDrawer'
import SendMessageModal from 'pages/Students/components/SendMessageModal/SendMessageModal'
import {
	downloadCounselorLorPdf,
	fetchCounselorLorLetter,
	patchCounselorLorLetter,
} from 'api/counselorLor'
import {
	downloadStudentLorFile,
	uploadCounselorLorExternal,
} from 'api/lorRequests'
import { buildAssignmentReminderMessage } from 'pages/ApplicationsPage/utils/assignmentReminderMessages'
import { recordReminderSent } from 'pages/ApplicationsPage/utils/assignmentReminderCooldown'
import { fmtDateTime } from 'pages/ApplicationDetailsPage/utils/applicationDetailsFormat'
import { ReactComponent as StarsIcon } from 'assets/icons/stars.svg'
import { ReactComponent as PersonAddIcon } from 'assets/icons/person.svg'
import { ReactComponent as ArrowShortRightIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as ContractIcon } from 'assets/icons/contract.svg'
import { AppDetailIcon } from 'pages/ApplicationDetailsPage/icons/AppDetailIcon'
import CourseLorRequirements from '../CourseLorRequirements/CourseLorRequirements'
import s from '../../styles/ApplicationRecommendationCard.module.scss'

const ApplicationRecommendationCard = ({
	card,
	studentId,
	studentName,
	onReload,
}) => {
	const [generateOpen, setGenerateOpen] = useState(false)
	const [viewLetter, setViewLetter] = useState(null)
	const [drawerMode, setDrawerMode] = useState('view')
	const [savedPillVisible, setSavedPillVisible] = useState(false)
	const [improveLetter, setImproveLetter] = useState(null)
	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		url: '',
		title: '',
	})
	const [assignOpen, setAssignOpen] = useState(false)
	const [assignKey, setAssignKey] = useState(0)
	const [bragsheetAssignOpen, setBragsheetAssignOpen] = useState(false)
	const [bragsheetAssignKey, setBragsheetAssignKey] = useState(0)
	const [reminderOpen, setReminderOpen] = useState(false)
	const [reminderMessage, setReminderMessage] = useState('')
	const [reminderAssignmentId, setReminderAssignmentId] = useState('')
	const [uploadingRowId, setUploadingRowId] = useState('')
	const [expanded, setExpanded] = useState(false)
	const [seenLorIds, setSeenLorIds] = useState(() => {
		try {
			const stored = window.localStorage.getItem(`seen-lors:${studentId}`)
			return new Set(stored ? JSON.parse(stored) : [])
		} catch {
			return new Set()
		}
	})
	const uploadInputRef = useRef(null)
	const uploadContextRef = useRef(null)

	const application = useMemo(
		() => ({
			applicationTargetId: card.applicationTargetId,
			studentId,
			studentName,
			university: card.university,
			program: card.program,
			intake: card.intake,
		}),
		[card, studentId, studentName],
	)

	const selectedStudents = useMemo(
		() => [{ id: studentId, name: studentName || 'Student' }],
		[studentId, studentName],
	)

	const openGenerate = useCallback(() => {
		setImproveLetter(null)
		setGenerateOpen(true)
	}, [])

	const closeGenerate = useCallback(() => {
		setGenerateOpen(false)
		setImproveLetter(null)
		onReload?.({ silent: true })
	}, [onReload])

	const handleLetterSaved = useCallback(() => {
		// Keep the generate modal/result drawer mounted; refresh list in background.
		onReload?.({ silent: true })
	}, [onReload])

	const handleAssignBragsheetFromLor = useCallback(() => {
		setGenerateOpen(false)
		setImproveLetter(null)
		setBragsheetAssignKey(value => value + 1)
		setBragsheetAssignOpen(true)
	}, [])

	const openAssignLor = useCallback(() => {
		setAssignKey(value => value + 1)
		setAssignOpen(true)
	}, [])

	const closeViewLetter = useCallback(() => {
		setViewLetter(null)
		setDrawerMode('view')
		setSavedPillVisible(false)
	}, [])

	const handleViewCounselorLetter = useCallback(
		async letter => {
			if (!studentId || !letter?.letterId) return
			try {
				const data = await fetchCounselorLorLetter(studentId, letter.letterId)
				setDrawerMode('view')
				setSavedPillVisible(false)
				setViewLetter(data?.letter || null)
			} catch (e) {
				console.error('Failed to open counselor LoR', e)
			}
		},
		[studentId],
	)

	const saveLetterSnapshot = useCallback(
		async snapshot => {
			if (!studentId || !viewLetter?.id) return null
			const response = await patchCounselorLorLetter(studentId, viewLetter.id, {
				expectedRevision: viewLetter.revision,
				title: viewLetter.title,
				contentJson: snapshot.contentJson,
				contentHtml: snapshot.contentHtml,
				contentText: snapshot.contentText,
				wordCount: snapshot.wordCount,
			})
			setViewLetter(response.letter)
			setSavedPillVisible(true)
			window.setTimeout(() => setSavedPillVisible(false), 1000)
			onReload?.()
			return response.letter
		},
		[onReload, studentId, viewLetter],
	)

	const copyViewLetter = useCallback(async () => {
		const text = String(viewLetter?.contentText || '').trim()
		if (!text) return
		try {
			await navigator.clipboard.writeText(text)
		} catch (e) {
			console.error('Failed to copy LoR', e)
		}
	}, [viewLetter?.contentText])

	const downloadViewLetter = useCallback(async () => {
		if (!studentId || !viewLetter?.id) return
		try {
			const blob = await downloadCounselorLorPdf(studentId, viewLetter.id)
			const url = URL.createObjectURL(blob)
			const anchor = document.createElement('a')
			anchor.href = url
			anchor.download = `LoR_${studentName || 'letter'}.pdf`
			document.body.appendChild(anchor)
			anchor.click()
			anchor.remove()
			URL.revokeObjectURL(url)
		} catch (e) {
			console.error('Failed to download LoR', e)
		}
	}, [studentId, studentName, viewLetter?.id])

	const improveViewLetter = useCallback(() => {
		if (!viewLetter) return
		setImproveLetter(viewLetter)
		setViewLetter(null)
		setGenerateOpen(true)
	}, [viewLetter])

	const showPdfPreview = useCallback((blob, title) => {
		const url = window.URL.createObjectURL(blob)
		setPdfPreview(prev => {
			if (prev?.url?.startsWith('blob:')) {
				try {
					window.URL.revokeObjectURL(prev.url)
				} catch {}
			}
			return { open: true, url, title: title || 'Document' }
		})
	}, [])

	const closePdfPreview = useCallback(() => {
		setPdfPreview(prev => {
			if (prev?.url?.startsWith('blob:')) {
				try {
					window.URL.revokeObjectURL(prev.url)
				} catch {}
			}
			return { open: false, url: '', title: '' }
		})
	}, [])

	const handleViewStudentLor = useCallback(
		async item => {
			if (!studentId || !item?.fileId) return
			try {
				const blob = await downloadStudentLorFile(studentId, item.fileId)
				showPdfPreview(blob, item.title || 'LoR')
				setSeenLorIds(current => {
					const next = new Set(current)
					next.add(String(item.id))
					try {
						window.localStorage.setItem(
							`seen-lors:${studentId}`,
							JSON.stringify([...next]),
						)
					} catch {}
					return next
				})
			} catch (e) {
				console.error('Failed to preview student LoR', e)
			}
		},
		[showPdfPreview, studentId],
	)

	const handleUploadClick = useCallback(item => {
		uploadContextRef.current = item
		uploadInputRef.current?.click()
	}, [])

	const handleUploadFile = useCallback(
		async event => {
			const file = event.target.files?.[0]
			event.target.value = ''
			const row = uploadContextRef.current
			if (!file || !studentId || !card.applicationTargetId) return
			setUploadingRowId(row?.id || 'upload')
			try {
				const formData = new FormData()
				formData.append('file', file)
				formData.append('applicationTargetId', card.applicationTargetId)
				if (card.university) formData.append('universityName', card.university)
				if (card.program) formData.append('courseName', card.program)
				await uploadCounselorLorExternal(studentId, formData)
				onReload?.()
			} catch (e) {
				console.error('Failed to upload LoR', e)
			} finally {
				setUploadingRowId('')
				uploadContextRef.current = null
			}
		},
		[
			card.applicationTargetId,
			card.program,
			card.university,
			onReload,
			studentId,
		],
	)

	const handleNudgeStudentLor = useCallback(
		item => {
			const message = buildAssignmentReminderMessage({
				status: 'not_started',
				studentName,
				applicationName: card.university,
				deadline: item?.lastUpdated,
			})
			setReminderMessage(message)
			setReminderAssignmentId(String(item?.assignmentId || ''))
			setReminderOpen(true)
		},
		[card.university, studentName],
	)

	const handleReminderSent = useCallback(() => {
		if (reminderAssignmentId) {
			recordReminderSent(reminderAssignmentId)
		}
	}, [reminderAssignmentId])

	const hasCounselorLetters = card.counselorLetters.length > 0
	const hasStudentBlock = card.studentRows.length > 0
	// Always expandable now: the course's own recommendation requirements live
	// in here, and they are worth checking before any letter exists.
	const canExpand = true
	const hasReceivedLetter = card.studentRows.some(
		row => row.counselorStatus === 'success',
	)
	const hasPendingRequest = card.studentRows.some(
		row => row.counselorStatus === 'assigned',
	)
	const incomingLorIds = card.studentRows
		.filter(row => row.counselorStatus === 'success')
		.map(row => String(row.id))
	const hasUnseenLor = incomingLorIds.some(id => !seenLorIds.has(id))
	const requestStatus = hasReceivedLetter
		? { label: 'Received', tone: 'received' }
		: hasPendingRequest
			? { label: 'Awaiting student', tone: 'pending' }
			: { label: 'Not assigned', tone: 'neutral' }

	return (
		<>
			<article className={s.applicationRow} role="row">
				<div className={s.summary}>
					<div className={s.application} role="cell">
						<div className={s.applicationTitle}>
							<h4 className={s.university}>
								{card.university || 'Application'}
							</h4>
							{hasUnseenLor ? <span className={s.newTag}>New</span> : null}
						</div>
						{card.program ? <p className={s.program}>{card.program}</p> : null}
						{card.intake ? <span className={s.intake}>{card.intake}</span> : null}
					</div>

					<div className={s.statusCell} role="cell">
						<span className={[s.status, s[requestStatus.tone]].join(' ')}>
							<span className={s.statusDot} aria-hidden="true" />
							{requestStatus.label}
						</span>
					</div>

					<div className={s.letterCount} role="cell">
						<strong>{card.counselorLetters.length}</strong>
						<span>
							{card.counselorLetters.length === 1
								? 'letter'
								: 'letters'}
						</span>
					</div>

					<div className={s.rowActions} role="cell">
						<button
							type="button"
							className={s.outlineBtn}
							onClick={openAssignLor}
						>
							<PersonAddIcon />
							{hasPendingRequest ? 'Reassign' : 'Assign'}
						</button>
						<button
							type="button"
							className={s.primaryBtn}
							onClick={openGenerate}
						>
							<StarsIcon />
							Create LoR
						</button>
						<button
							type="button"
							className={`${s.expandButton} ${
								canExpand ? '' : s.expandButtonDisabled
							}`.trim()}
							onClick={() => canExpand && setExpanded(current => !current)}
							aria-expanded={canExpand ? expanded : undefined}
							aria-disabled={!canExpand}
							tabIndex={canExpand ? undefined : -1}
							aria-label={
								expanded ? 'Hide recommendations' : 'Show recommendations'
							}
							title={canExpand ? (expanded ? 'Collapse' : 'Expand') : undefined}
						>
							<span
								className={`${s.expandButton__icon} ${
									expanded ? s.expandedIcon : ''
								}`.trim()}
							>
								<ArrowShortRightIcon />
							</span>
						</button>
					</div>
				</div>

				{expanded ? (
					<div
						className={`${s.details} ${
							hasCounselorLetters !== hasStudentBlock ? s.solo : ''
						}`.trim()}
					>
						<CourseLorRequirements
							studentId={studentId}
							applicationTargetId={card.applicationTargetId}
						/>
						{hasCounselorLetters ? (
							<section className={s.letterSection}>
								<p className={s.detailLabel}>Letters written</p>
								<div className={s.letterList}>
									{card.counselorLetters.map(letter => (
										<div className={s.letterRow} key={letter.id}>
											<span className={s.letterIcon} aria-hidden="true">
												<ContractIcon />
											</span>
											<div className={s.letterInfo}>
												<span className={s.letterTitle}>{letter.title}</span>
												<span className={s.letterMeta}>
													<span className={s.letterAuthor}>
														Author: {letter.author}
													</span>
													<span className={s.metaDivider}>·</span>
													<span className={s.letterDate}>
														<AppDetailIcon
															name="schedule"
															size={14}
															className={s.dateIcon}
														/>
														{fmtDateTime(letter.updatedAt)}
													</span>
												</span>
											</div>
											<button
												type="button"
												className={s.viewBtn}
												onClick={() => handleViewCounselorLetter(letter)}
											>
												View
											</button>
										</div>
									))}
								</div>
							</section>
						) : null}

						{hasStudentBlock ? (
							<section className={s.requestSection}>
								<p className={s.detailLabel}>Student request</p>
								<div className={[list.rows, s.docRows].join(' ')}>
									{card.studentRows.map(row => (
										<DocumentChecklistRow
											key={row.id}
											item={row}
											className={list['row--recommend']}
											onAssign={openAssignLor}
											onUploadClick={handleUploadClick}
											onNudge={() => handleNudgeStudentLor(row)}
											onReupload={openAssignLor}
											onView={handleViewStudentLor}
											isUploading={uploadingRowId === row.id}
										/>
									))}
								</div>
							</section>
						) : null}
					</div>
				) : null}
			</article>

			<input
				ref={uploadInputRef}
				type="file"
				accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
				className={s.hiddenInput}
				onChange={handleUploadFile}
			/>

			<GenerateLorModal
				isOpen={generateOpen}
				onClose={closeGenerate}
				application={application}
				onLetterSaved={handleLetterSaved}
				initialLetter={improveLetter}
				onAssignBragsheet={handleAssignBragsheetFromLor}
			/>

			{viewLetter && !generateOpen ? (
				<ResultDrawer
					application={application}
					letter={viewLetter}
					drawerMode={drawerMode}
					savedPillVisible={savedPillVisible}
					onClose={closeViewLetter}
					onEdit={() => setDrawerMode('edit')}
					onImprove={improveViewLetter}
					onCopy={copyViewLetter}
					onDownload={downloadViewLetter}
					onSaveSnapshot={saveLetterSnapshot}
					setDrawerMode={setDrawerMode}
				/>
			) : null}

			<PdfPreviewPortal
				open={pdfPreview.open}
				url={pdfPreview.url}
				title={pdfPreview.title}
				onClose={closePdfPreview}
			/>

			<AssignActivityModal
				key={bragsheetAssignKey}
				isOpen={bragsheetAssignOpen}
				onClose={() => setBragsheetAssignOpen(false)}
				selectedStudents={selectedStudents}
				lockRecipients
				initialAssignmentType="bragsheet_builder"
				initialTaskTitle="Complete Brag Sheet for LoR"
				initialStepId="timeline"
				onSuccess={() => {
					setBragsheetAssignOpen(false)
					onReload?.()
				}}
			/>

			<AssignActivityModal
				key={assignKey}
				isOpen={assignOpen}
				onClose={() => setAssignOpen(false)}
				selectedStudents={selectedStudents}
				lockRecipients
				tepeModal="applications"
				initialAssignmentType="LoR"
				initialApplicationTargetId={card.applicationTargetId}
				initialUniversityName={card.university}
				onSuccess={() => {
					setAssignOpen(false)
					onReload?.()
				}}
			/>

			<SendMessageModal
				isOpen={reminderOpen}
				onClose={() => setReminderOpen(false)}
				selectedStudents={selectedStudents}
				initialMessage={reminderMessage}
				onAfterSendSuccess={handleReminderSent}
			/>
		</>
	)
}

export default ApplicationRecommendationCard
